const SECOND_LEVEL_SUFFIXES = ["com", "co", "org", "net", "gov", "edu", "ac"];

const isLocalhost = (hostname: string): boolean => hostname === "localhost";

// The SSO server is a sibling subdomain of the SPA (sso.<domain> vs <sub>.<domain>), so the
// trusted zone is the current host's parent domain. Refuses a bare public suffix (com.au, co.uk).
const trustedZone = (currentHostname: string): string => {
  const labels = currentHostname.split(".");
  if (labels.length < 3) return currentHostname;
  const parent = labels.slice(1);
  const isPublicSuffix =
    parent.length === 2 && SECOND_LEVEL_SUFFIXES.includes(parent[0]) && parent[1].length === 2;
  return isPublicSuffix ? currentHostname : parent.join(".");
};

const decodeBase64 = (value: string): string | null => {
  try {
    return atob(value);
  } catch {
    return null;
  }
};

/**
 * Decodes the base64 `returnUrl` and returns it with `user_token` appended, or null when it
 * does not point at a trusted host. Never returns a URL for an unvalidated destination.
 */
export const buildSsoReturnUrl = (
  encodedReturnUrl: unknown,
  userToken: string,
  currentHostname: string,
  extraHosts: string[] = []
): string | null => {
  if (typeof encodedReturnUrl !== "string" || !encodedReturnUrl || !userToken) return null;

  const decoded = decodeBase64(encodedReturnUrl);
  if (!decoded) return null;

  let url: URL;
  try {
    url = new URL(decoded);
  } catch {
    return null;
  }

  const host = url.hostname.toLowerCase();
  const current = currentHostname.toLowerCase();
  const zone = trustedZone(current);

  const schemeOk = url.protocol === "https:" || (url.protocol === "http:" && isLocalhost(host));
  const hostOk =
    host === current ||
    host === zone ||
    host.endsWith("." + zone) ||
    extraHosts.map((h) => h.trim().toLowerCase()).includes(host);

  if (!schemeOk || !hostOk || url.username || url.password) return null;

  url.searchParams.set("user_token", userToken);
  return url.toString();
};
